import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  doubtfulDebtReserves,
  type DoubtfulDebtReserveItem,
} from '@/api/doubtfulDebtReserves';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import SearchSelect from '@/components/SearchSelect';
import { api } from '@/api/client';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  expense_account_code: string;
  reserve_account_code: string;
  comment: string;
  items: DoubtfulDebtReserveItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  expense_account_code: '944',
  reserve_account_code: '381',
  comment: '',
  items: [],
};

const newRow = (n: number): DoubtfulDebtReserveItem => ({
  line_number: n,
  contractor_id: '',
  contract_id: '',
  debt_account_code: '361',
  debt_amount: '0',
  reserve_percent: '100',
  reserve_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DoubtfulDebtReserveEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const docQuery = useQuery({
    queryKey: ['doubtful-debt-reserve', id],
    queryFn: () => doubtfulDebtReserves.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'doubtful-debt-reserve', (cid) => doubtfulDebtReserves.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [acctTab, setAcctTab] = useState(false);
  const numberPreview = useNumberPreview('doc.doubtful_debt_reserves', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        expense_account_code: d.expense_account_code,
        reserve_account_code: d.reserve_account_code,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          contractor_id: it.contractor_id,
          contract_id: it.contract_id,
          debt_account_code: it.debt_account_code,
          debt_amount: String(it.debt_amount),
          reserve_percent: String(it.reserve_percent),
          reserve_amount: String(it.reserve_amount),
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const [fillBusy, setFillBusy] = useState(false);

  // «Заповнити з монітора розрахунків» (рішення власника 2026-08-19):
  // всі ДЕБІТОРИ (сальдо > 0) на дату документа → рядки з боргом;
  // % резерву бухгалтер ставить сам (типово 100). Договір — якщо у
  // контрагента він ОДИН, підставляється автоматично.
  const fillFromMonitor = async () => {
    setFillBusy(true);
    setError(null);
    try {
      const to = form.document_date.slice(0, 10);
      const r = await api.get<{ items: {
        contractor_id: string; name: string; closing: string }[] }>(
        '/reports/reconciliation-act/summary', { params: {
          organization_id: form.organization_id,
          date_from: '1990-01-01', date_to: to,
          only_debts: true, limit: 200, offset: 0 } });
      const rows = (r.data.items ?? []).filter((d) => Number(d.closing) > 0);
      if (!rows.length) {
        setError('Дебіторів із боргом на дату документа не знайдено.');
        return;
      }
      // договори — по одному запиту на контрагента (боржників десятки)
      const contracts = await Promise.all(rows.map((d) =>
        refs.contracts({ organization_id: form.organization_id,
          contractor_id: d.contractor_id }).catch(() => [])));
      setForm((f) => ({
        ...f,
        items: rows.map((d, i) => ({
          line_number: i + 1,
          contractor_id: d.contractor_id,
          contract_id: contracts[i]?.length === 1 ? contracts[i]![0]!.id : '',
          debt_account_code: '361',
          debt_amount: String(Number(d.closing).toFixed(2)),
          reserve_percent: '100',
          reserve_amount: String(Number(d.closing).toFixed(2)),
        })),
      }));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setFillBusy(false);
    }
  };

  const uniqueContractorIds = useMemo(() => {
    const s = new Set<string>();
    form.items.forEach((it) => it.contractor_id && s.add(it.contractor_id));
    return Array.from(s);
  }, [form.items]);

  const contractQueries = useQueries({
    queries: uniqueContractorIds.map((cid) => ({
      queryKey: ['contracts', form.organization_id, cid],
      queryFn: () => refs.contracts({ organization_id: form.organization_id, contractor_id: cid }),
      enabled: !!form.organization_id && !!cid,
    })),
  });
  const contractsByContractor = useMemo(() => {
    const m: Record<string, { id: string; code: string; name: string }[]> = {};
    uniqueContractorIds.forEach((cid, idx) => {
      const q = contractQueries[idx];
      if (q?.data) m[cid] = q.data;
    });
    return m;
  }, [uniqueContractorIds, contractQueries]);

  const total = form.items.reduce((s, e) => s + Number(e.reserve_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => doubtfulDebtReserves.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      expense_account_code: form.expense_account_code,
      reserve_account_code: form.reserve_account_code,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate(`/doubtful-debt-reserves/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => doubtfulDebtReserves.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      expense_account_code: form.expense_account_code,
      reserve_account_code: form.reserve_account_code,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await doubtfulDebtReserves.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        expense_account_code: form.expense_account_code,
        reserve_account_code: form.reserve_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await doubtfulDebtReserves.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/doubtful-debt-reserves/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate(`/doubtful-debt-reserves/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await doubtfulDebtReserves.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        expense_account_code: form.expense_account_code,
        reserve_account_code: form.reserve_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      await doubtfulDebtReserves.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] });
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => doubtfulDebtReserves.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] });
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => doubtfulDebtReserves.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate('/doubtful-debt-reserves');
    },
  });

  function setItem(i: number, patch: Partial<DoubtfulDebtReserveItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('contractor_id' in patch) next.contract_id = '';
        if ('debt_amount' in patch || 'reserve_percent' in patch) {
          const d = Number(next.debt_amount || 0);
          const p = Number(next.reserve_percent || 0);
          next.reserve_amount = ((d * p) / 100).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати резерв сумнівних боргів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/doubtful-debt-reserves')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.917 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Сумнівні борги', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.917: шапка одним рядком — назва ліворуч, тулбар праворуч. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий резерв сумнівних боргів' : `Резерв: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.doubtful_debt_reserves', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/doubtful-debt-reserves')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postDisabled={form.items.length === 0}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/doubtful-debt-reserves/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.doubtful_debt_reserves', id: isNew ? '' : id! }} />
      }>
      {/* ч.917 — блоками (коментар — в останньому блоці). */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid doc-grid--5">
        <div className="field">
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      {/* ч.917: дії таблиці («+ додати рядок», заповнення) — у шапці блоку. */}
      <DocBlock index={2} title="Сумнівні борги"
        actions={!isPosted ? (
          <>
            <button type="button" className="btn-doc" disabled={fillBusy || !form.organization_id}
              title="Підтягнути ВСІХ боржників (дебіторка, сальдо > 0) з монітора розрахунків: сума боргу = сальдо, договір підставляється, якщо у контрагента він один"
              onClick={() => void fillFromMonitor()}>
              {fillBusy ? 'заповнення…' : 'заповнити з монітора розрахунків'}
            </button>
            <button type="button" className="primary" onClick={addRow}>+ додати рядок</button>
          </>
        ) : undefined}>
      {/* 2026-08-09 (рішення власника): рахунки обліку — єдиною вкладкою. ч.917: «дт-кт» —
          у рядку назви (entriesDoc тулбара), «історія» — у правій колонці. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={acctTab ? 'active' : ''}
            onClick={() => setAcctTab((v) => !v)}>Рахунки обліку</button>
        </div>
      </div>
      {acctTab && (
        <div className="doc-grid" style={{ marginTop: '0.5rem' }}>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рах. витрат</label>
            <input value={form.expense_account_code}
                   onChange={(e) => setForm({ ...form, expense_account_code: e.target.value })}
                   disabled={isPosted} placeholder="944" />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Рах. резерву</label>
            <input value={form.reserve_account_code}
                   onChange={(e) => setForm({ ...form, reserve_account_code: e.target.value })}
                   disabled={isPosted} placeholder="381" />
          </div>
        </div>
      )}
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        У кожному рядку — контрагент і його борг. Сума резерву = борг × процент / 100.
        При проведенні створюється проводка Дт &lt;рах. витрат&gt; — Кт &lt;рах. резерву&gt;
        з субконто контрагент+договір на кредиті.
      </p>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Контрагент</th>
            <th>Договір</th>
            <th style={{ width: 80 }}>Рах. боргу</th>
            <th style={{ width: 130 }}>Сума боргу</th>
            <th style={{ width: 90 }}>%</th>
            <th style={{ width: 130 }}>Сума резерву</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const contracts = contractsByContractor[it.contractor_id] ?? [];
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>
                  <SearchSelect
                    items={(contractorsQ.data?.items ?? []).map((c) => ({
                      id: c.id, label: c.name, hint: c.edrpou ?? null,
                    }))}
                    value={it.contractor_id}
                    onChange={(v) => setItem(i, { contractor_id: v })}
                    disabled={isPosted}
                  />
                </td>
                <td>
                  <select value={it.contract_id}
                          onChange={(e) => setItem(i, { contract_id: e.target.value })}
                          disabled={isPosted || !it.contractor_id} style={{ width: '100%' }}>
                    <option value="">—</option>
                    {contracts.map((c) => (
                      <option key={c.id} value={c.id}>{contractLabel(c)}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input value={it.debt_account_code}
                         onChange={(e) => setItem(i, { debt_account_code: e.target.value })}
                         disabled={isPosted} style={{ width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.debt_amount}
                         onChange={(e) => setItem(i, { debt_amount: e.target.value })}
                         disabled={isPosted} style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.reserve_percent}
                         onChange={(e) => setItem(i, { reserve_percent: e.target.value })}
                         disabled={isPosted} style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmt(Number(it.reserve_amount))}
                </td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} style={{ textAlign: 'right' }}>Усього резерв:</td>
            <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.doubtful_debt_reserves" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
